import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useI18n } from '../context/I18nContext';

// Tela Publish - doc 12 secao 6 (Pagina do Facebook) e doc 13 secao 2 (Instagram).
//
// O fluxo do Instagram tem tres passos obrigatorios da Meta: criar o container,
// esperar status_code FINISHED e so entao publicar. O polling vive AQUI, e nao
// no backend, de proposito: Reels leva ate dois minutos e uma requisicao HTTP
// pendurada esse tempo parece travamento - justamente no video que a Meta vai
// analisar. Aqui a tela mostra o segundo em que esta.

const ESPERA_MS = 3000;
const TENTATIVAS_FOTO = 20;   // ~60s
const TENTATIVAS_VIDEO = 45;  // ~135s, Reels demora mais

const MetaPublish = () => {
  const { t } = useI18n();
  const [conexoes, setConexoes] = useState([]);
  const [conexaoId, setConexaoId] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [progresso, setProgresso] = useState('');
  const [erro, setErro] = useState('');
  const [resultado, setResultado] = useState(null);

  // Facebook
  const [fbTexto, setFbTexto] = useState('');
  const [fbLink, setFbLink] = useState('');
  const [fbFoto, setFbFoto] = useState('');
  const [posts, setPosts] = useState([]);

  // Instagram
  const [igTipo, setIgTipo] = useState('foto');
  const [igUrl, setIgUrl] = useState('');
  const [igUrls, setIgUrls] = useState('');
  const [igLegenda, setIgLegenda] = useState('');

  const conexao = conexoes.find((c) => String(c.id) === String(conexaoId));
  const ehIG = conexao && conexao.platform === 'instagram';

  useEffect(() => {
    api.get('/meta-connections')
      .then((r) => {
        const conectadas = (r.data || []).filter(
          (c) => c.status === 'connected' && c.platform !== 'whatsapp'
        );
        setConexoes(conectadas);
        if (conectadas.length) setConexaoId(String(conectadas[0].id));
      })
      .catch(() => setErro(t('metainbox.a2')));
  }, [t]);

  function falhou(err) {
    const e = err.response && err.response.data && err.response.data.error;
    setErro((e && (e.message || e.error_user_msg)) || err.message);
    setOcupado(false);
    setProgresso('');
  }

  const carregarPosts = useCallback(async () => {
    setErro('');
    try {
      const r = await api.get(`/meta-connections/${conexaoId}/posts`);
      const lista = r.data.posts || [];
      // Comentarios vem por post: a tela de moderacao precisa deles junto,
      // senao o video mostra a lista e nenhum botao para agir.
      const comComentarios = await Promise.all(lista.map(async (p) => {
        if (!p.comentarios) return { ...p, comentariosLista: [] };
        try {
          const c = await api.get(`/meta-connections/${conexaoId}/posts/${p.id}/comments`);
          return { ...p, comentariosLista: c.data.comentarios || [] };
        } catch (e) {
          return { ...p, comentariosLista: [] };
        }
      }));
      setPosts(comComentarios);
    } catch (err) {
      falhou(err);
    }
  }, [conexaoId]);

  async function publicarFacebook(e) {
    e.preventDefault();
    setOcupado(true); setErro(''); setResultado(null);
    try {
      let r;
      if (fbFoto.trim()) {
        setProgresso(t('pub.publishing'));
        r = await api.post(`/meta-connections/${conexaoId}/posts/photo`, {
          url: fbFoto.trim(), caption: fbTexto
        });
      } else {
        setProgresso(t('pub.publishing'));
        r = await api.post(`/meta-connections/${conexaoId}/posts`, {
          message: fbTexto, link: fbLink.trim() || undefined
        });
      }
      setResultado({ link: r.data.link, rede: 'facebook' });
      setFbTexto(''); setFbLink(''); setFbFoto('');
      carregarPosts();
    } catch (err) {
      falhou(err);
      return;
    }
    setOcupado(false); setProgresso('');
  }

  async function esperarContainer(creationId, tentativas) {
    for (let i = 0; i < tentativas; i++) {
      setProgresso(t('pub.step2', { v1: Math.round((i * ESPERA_MS) / 1000) }));
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, ESPERA_MS));
      // eslint-disable-next-line no-await-in-loop
      const s = await api.get(`/meta-connections/${conexaoId}/ig/media/${creationId}`);
      if (s.data.pronto) return true;
      if (s.data.statusCode === 'ERROR' || s.data.statusCode === 'EXPIRED') {
        throw new Error(t('pub.mediaError', { v1: s.data.status || s.data.statusCode }));
      }
    }
    throw new Error(t('pub.timeout'));
  }

  async function publicarInstagram(e) {
    e.preventDefault();
    setOcupado(true); setErro(''); setResultado(null);
    try {
      setProgresso(t('pub.step1'));
      let creationId;

      if (igTipo === 'carrossel') {
        const urls = igUrls.split('\n').map((u) => u.trim()).filter(Boolean);
        const filhos = [];
        for (const u of urls) {
          // eslint-disable-next-line no-await-in-loop
          const f = await api.post(`/meta-connections/${conexaoId}/ig/media`, {
            tipo: 'filho', imageUrl: u
          });
          filhos.push(f.data.creationId);
          // eslint-disable-next-line no-await-in-loop
          await esperarContainer(f.data.creationId, TENTATIVAS_FOTO);
        }
        const pai = await api.post(`/meta-connections/${conexaoId}/ig/media`, {
          tipo: 'carrossel', filhos, caption: igLegenda
        });
        creationId = pai.data.creationId;
        await esperarContainer(creationId, TENTATIVAS_FOTO);
      } else if (igTipo === 'reels') {
        const c = await api.post(`/meta-connections/${conexaoId}/ig/media`, {
          tipo: 'reels', videoUrl: igUrl.trim(), caption: igLegenda
        });
        creationId = c.data.creationId;
        await esperarContainer(creationId, TENTATIVAS_VIDEO);
      } else {
        const c = await api.post(`/meta-connections/${conexaoId}/ig/media`, {
          imageUrl: igUrl.trim(), caption: igLegenda
        });
        creationId = c.data.creationId;
        await esperarContainer(creationId, TENTATIVAS_FOTO);
      }

      setProgresso(t('pub.step3'));
      const pub = await api.post(`/meta-connections/${conexaoId}/ig/publish`, { creationId });
      setResultado({ link: pub.data.link, rede: 'instagram' });
      setIgUrl(''); setIgUrls(''); setIgLegenda('');
    } catch (err) {
      if (err.response) { falhou(err); return; }
      setErro(err.message); setOcupado(false); setProgresso(''); return;
    }
    setOcupado(false); setProgresso('');
  }

  async function moderar(comentarioId, acao) {
    setErro('');
    try {
      if (acao === 'excluir') {
        // eslint-disable-next-line no-restricted-globals
        if (!window.confirm(t('pub.confirmDel'))) return;
        await api.delete(`/meta-connections/${conexaoId}/comments/${comentarioId}`);
      } else {
        await api.post(`/meta-connections/${conexaoId}/comments/${comentarioId}/hide`, {
          hidden: acao === 'ocultar'
        });
      }
      carregarPosts();
    } catch (err) {
      falhou(err);
    }
  }

  const campo = 'w-full px-4 py-3 bg-white rounded-lg border border-gray-200 text-sm';
  const botao = 'px-5 py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 disabled:opacity-50 transition-all';

  return (
    <div className="p-8 max-w-4xl">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">{t('pub.title')}</h1>

      {conexoes.length === 0 ? (
        <p className="text-gray-500">{t('pub.noConn')}</p>
      ) : (
        <>
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 mb-6">
            <label className="block text-sm font-semibold text-gray-900 mb-2">{t('pub.pickConn')}</label>
            <select value={conexaoId} onChange={(e) => { setConexaoId(e.target.value); setPosts([]); setResultado(null); }}
              className={campo}>
              {conexoes.map((c) => (
                <option key={c.id} value={c.id}>{c.name} ({c.platform})</option>
              ))}
            </select>
          </div>

          {erro && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{erro}</div>
          )}
          {progresso && (
            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 text-blue-700 rounded-lg text-sm">{progresso}</div>
          )}
          {resultado && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 text-green-800 rounded-lg text-sm flex items-center gap-3">
              <span className="font-semibold">{t('pub.published')}</span>
              {resultado.link && (
                <a href={resultado.link} target="_blank" rel="noreferrer" className="underline">
                  {resultado.rede === 'instagram' ? t('pub.viewOnIg') : t('pub.viewOnFb')}
                </a>
              )}
            </div>
          )}

          {ehIG ? (
            <form onSubmit={publicarInstagram} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 space-y-3">
              <h2 className="text-lg font-semibold text-gray-900">{t('pub.igTitle')}</h2>
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-1">{t('pub.igType')}</label>
                <select value={igTipo} onChange={(e) => setIgTipo(e.target.value)} className={campo}>
                  <option value="foto">{t('pub.igPhoto')}</option>
                  <option value="carrossel">{t('pub.igCarousel')}</option>
                  <option value="reels">{t('pub.igReels')}</option>
                </select>
              </div>
              {igTipo === 'carrossel' ? (
                <textarea rows={4} value={igUrls} onChange={(e) => setIgUrls(e.target.value)}
                  placeholder={t('pub.igImages')} className={campo} />
              ) : (
                <input type="url" value={igUrl} onChange={(e) => setIgUrl(e.target.value)}
                  placeholder={igTipo === 'reels' ? t('pub.igVideoUrl') : t('pub.igImageUrl')} className={campo} />
              )}
              <textarea rows={3} value={igLegenda} onChange={(e) => setIgLegenda(e.target.value)}
                placeholder={t('pub.igCaption')} className={campo} />
              <button type="submit" disabled={ocupado} className={botao}>
                {ocupado ? t('pub.publishing') : t('pub.publish')}
              </button>
            </form>
          ) : (
            <>
              <form onSubmit={publicarFacebook} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 space-y-3">
                <h2 className="text-lg font-semibold text-gray-900">{t('pub.fbPost')}</h2>
                <textarea rows={3} value={fbTexto} onChange={(e) => setFbTexto(e.target.value)}
                  placeholder={t('pub.fbMessage')} className={campo} />
                <input type="url" value={fbLink} onChange={(e) => setFbLink(e.target.value)}
                  placeholder={t('pub.fbLink')} className={campo} />
                <input type="url" value={fbFoto} onChange={(e) => setFbFoto(e.target.value)}
                  placeholder={t('pub.fbPhoto')} className={campo} />
                <button type="submit" disabled={ocupado} className={botao}>
                  {ocupado ? t('pub.publishing') : t('pub.publish')}
                </button>
              </form>

              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 mt-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900">{t('pub.posts')}</h2>
                  <button type="button" onClick={carregarPosts}
                    className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200">
                    {t('pub.loadPosts')}
                  </button>
                </div>
                {posts.length === 0 ? (
                  <p className="text-sm text-gray-500">{t('pub.noPosts')}</p>
                ) : (
                  <ul className="space-y-3">
                    {posts.map((p) => (
                      <li key={p.id} className="border border-gray-100 rounded-xl p-4">
                        <p className="text-sm text-gray-800 mb-1">{p.mensagem || '—'}</p>
                        <div className="flex items-center gap-3 text-xs text-gray-500">
                          <span>{t('pub.comments', { v1: p.comentarios })}</span>
                          {p.link && (
                            <a href={p.link} target="_blank" rel="noreferrer" className="underline">
                              {t('pub.viewOnFb')}
                            </a>
                          )}
                        </div>
                        {p.comentariosLista && p.comentariosLista.map((c) => (
                          <div key={c.id} className="mt-2 flex items-center gap-2 text-xs">
                            <span className="flex-1 text-gray-700">{c.message}</span>
                            <button onClick={() => moderar(c.id, c.oculto ? 'reexibir' : 'ocultar')} className="underline text-gray-500">
                              {c.oculto ? t('pub.unhide') : t('pub.hide')}
                            </button>
                            <button onClick={() => moderar(c.id, 'excluir')} className="underline text-red-600">
                              {t('pub.deleteCm')}
                            </button>
                          </div>
                        ))}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default MetaPublish;
